import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, RefreshCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin-only panel showing recent client-side errors collected from the
// /api/log/error endpoint. Lives on the AdminDashboard so admins can spot
// runtime crashes (e.g., the blank-iPad-screen kind) without setting up
// Sentry/Datadog. Auth uses the same Bearer-token pattern as the rest of
// the admin pages.

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const RecentErrorsPanel = () => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const { data } = await axios.get(`${API}/admin/errors?limit=100`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setErrors(data?.errors || []);
    } catch (err) {
      toast.error("Couldn't load recent errors.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  const clearAll = async () => {
    if (!window.confirm("Clear all stored client errors? This can't be undone.")) return;
    try {
      const token = localStorage.getItem("session_token");
      await axios.delete(`${API}/admin/errors`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success("All errors cleared.");
      setErrors([]);
    } catch {
      toast.error("Couldn't clear errors.");
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 p-6 mb-6" data-testid="recent-errors-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-outfit font-semibold text-xl text-[#1B2349] flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-[#D98880]" />
          Recent client errors
          {errors.length > 0 && (
            <span className="ml-1 text-sm font-normal text-[#718096]">({errors.length})</span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchErrors}
            disabled={loading}
            className="text-sm text-[#1B2349] hover:bg-slate-100 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="recent-errors-refresh-btn"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {errors.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-sm text-[#D98880] hover:bg-rose-50 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
              data-testid="recent-errors-clear-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </button>
          )}
        </div>
      </div>

      {loading && errors.length === 0 ? (
        <p className="text-sm text-[#718096] py-6 text-center">Loading…</p>
      ) : errors.length === 0 ? (
        <p className="text-sm text-[#718096] py-6 text-center">
          No client errors recorded. Things are quiet 🌿
        </p>
      ) : (
        <ul className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
          {errors.map((e, i) => (
            <li
              key={`${e.ts}-${i}`}
              className="rounded-lg border border-slate-100 bg-slate-50/50 p-3"
              data-testid={`recent-error-row-${i}`}
            >
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <p className="text-sm font-medium text-[#1B2349] break-all">{e.message}</p>
                <span className="text-xs text-[#718096] whitespace-nowrap">{fmtTime(e.ts)}</span>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#718096]">
                {e.path && <span>path: <span className="font-mono">{e.path}</span></span>}
                {e.app_version && <span>v{e.app_version}</span>}
                {e.user_agent && (
                  <span className="truncate max-w-md">
                    UA: <span className="font-mono">{e.user_agent.slice(0, 80)}</span>
                  </span>
                )}
              </div>
              {e.stack && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-[#3090A8]">Stack trace</summary>
                  <pre className="mt-1 whitespace-pre-wrap font-mono text-[10px] text-[#4A5568] leading-snug">
                    {e.stack}
                  </pre>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RecentErrorsPanel;
